import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Alert,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ContactPhone as ContactIcon,
  Place as SiteIcon,
} from "@mui/icons-material";
import config from "../../../config/config.json";
import { apiCall } from '../../../services/ApiCall';

const ViewSiteDetailsPage = ({ status, viewData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [siteData, setSiteData] = useState({});
  const [siteUserData, setSiteUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const primaryColor = "#0d6efd";
  const secondaryColor = "#6c757d";

  // Original API function (unchanged logic)
  const getCompanyContact = async () => {
    setLoading(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POST_LOGIN_API_URL}getSitesContact`,
        {},
        { siteId: viewData.id },
        "GET"
      );
      if (isSuccess) {
        setSiteUserData(data?.data?.sitesUserData || []);
        setSiteData(data?.data?.sitesData || {});
        setError(null);
        console.log(data?.data?.sitesData);
      } else {
        setError("Failed to load site details");
        console.log("error");
      }
    } catch (err) {
      setError("An error occurred while loading site details");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Original useEffect (unchanged logic)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      Navigate("/");
    } else {
      getCompanyContact();
    }
  }, [viewData.id]);

  const InfoItem = ({ icon, label, value, fullWidth = false }) => (
    <Grid item xs={12} sm={fullWidth ? 12 : 6} md={fullWidth ? 12 : 4}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1 }}>
        <Box sx={{ 
          color: primaryColor, 
          mt: 0.5,
          minWidth: 24,
          display: 'flex',
          justifyContent: 'center'
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600, 
              color: secondaryColor,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              display: 'block',
              mb: 0.5
            }}
          >
            {label}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: primaryColor,
              fontWeight: 500,
              wordBreak: 'break-word'
            }}
          >
            {value || 'N/A'}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );

  const ContactCard = ({ user, index }) => (
    <Card 
      key={user.id || index}
      elevation={0}
      sx={{ 
        border: `1px solid ${primaryColor}20`,
        borderRadius: 2,
        mb: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[2],
          borderColor: `${primaryColor}40`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: `${primaryColor}15`,
              color: primaryColor,
              mr: 2,
              width: 48,
              height: 48
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 600 }}>
              {user.name || 'Unknown Contact'}
            </Typography>
            <Chip 
              label={user.designation || 'No designation'}
              size="small"
              icon={<BadgeIcon />}
              sx={{ 
                backgroundColor: `${primaryColor}10`,
                color: primaryColor,
                fontSize: '0.75rem',
                height: 24
              }}
            />
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <EmailIcon sx={{ color: secondaryColor, fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Email
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {user.email || 'No email provided'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
              <PhoneIcon sx={{ color: secondaryColor, fontSize: 20 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Phone
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {user.phone || 'No phone provided'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '100%' }}>
      {/* Site Details Section */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        {/* Header */}
        <Box sx={{ 
          bgcolor: primaryColor,
          color: 'white',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <SiteIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Site Details
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ 
          bgcolor: '#f8f9fa',
          p: { xs: 2, md: 3 }
        }}>
          <Grid container spacing={2}>
            <InfoItem 
              icon={<BusinessIcon fontSize="small" />}
              label="ARA ID"
              value={siteData?.araId}
            />
            <InfoItem 
              icon={<BusinessIcon fontSize="small" />}
              label="Company Name"
              value={siteData?.companyName}
            />
            <InfoItem 
              icon={<LocationIcon fontSize="small" />}
              label="Address"
              value={siteData?.address}
            />
            <InfoItem 
              icon={<LocationIcon fontSize="small" />}
              label="Street 2"
              value={siteData?.street2}
            />
            <InfoItem 
              icon={<LocationIcon fontSize="small" />}
              label="City"
              value={siteData?.city}
            />
            <InfoItem 
              icon={<LocationIcon fontSize="small" />}
              label="State"
              value={siteData?.state}
            />
            <InfoItem 
              icon={<LocationIcon fontSize="small" />}
              label="Zipcode"
              value={siteData?.zipcode}
            />
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1 }}>
                <Box sx={{ 
                  color: primaryColor, 
                  mt: 0.5,
                  minWidth: 24,
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {siteData?.status === 0 ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 600, 
                      color: secondaryColor,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      display: 'block',
                      mb: 0.5
                    }}
                  >
                    Status
                  </Typography>
                  <Chip
                    label={siteData?.status === 0 ? "Active" : "Inactive"}
                    color={siteData?.status === 0 ? "success" : "error"}
                    variant="outlined"
                    size="small"
                    icon={siteData?.status === 0 ? <ActiveIcon /> : <InactiveIcon />}
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Site Contacts Section */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ 
          bgcolor: primaryColor,
          color: 'white',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ContactIcon sx={{ fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Site Contacts
            </Typography>
          </Box>
          <Chip 
            label={`${siteUserData.length} Contact${siteUserData.length !== 1 ? 's' : ''}`}
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 500
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ 
          bgcolor: '#f8f9fa',
          p: { xs: 2, md: 3 },
          minHeight: 120
        }}>
          {siteUserData && siteUserData.length > 0 ? (
            <Stack spacing={0}>
              {siteUserData.map((user, index) => (
                <ContactCard key={user.id || index} user={user} index={index} />
              ))}
            </Stack>
          ) : (
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
              textAlign: 'center'
            }}>
              <ContactIcon sx={{ 
                fontSize: 64, 
                color: theme.palette.grey[300],
                mb: 2
              }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: theme.palette.grey[500],
                  fontWeight: 500,
                  mb: 1
                }}
              >
                No Contacts Added
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
              >
                There are no contacts associated with this site yet.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ViewSiteDetailsPage;